import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiMail, FiCalendar, FiEdit2, FiSave, FiX, FiTrendingUp, FiTarget } = FiIcons;

const UserProfile = ({ user, onUpdateUser, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: user.name,
    email: user.email
  });

  const handleSave = () => {
    const updatedUser = { ...user, ...editData };
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    onUpdateUser(updatedUser);
    setIsEditing(false);
  };

  const getBMIStats = () => {
    const history = JSON.parse(localStorage.getItem('bmiHistory') || '[]');
    if (history.length === 0) return null;

    const latest = history[0];
    const oldest = history[history.length - 1];
    const change = latest.bmi - oldest.bmi;

    return {
      current: latest.bmi,
      change: change.toFixed(1),
      totalCalculations: history.length,
      trend: change < 0 ? 'down' : change > 0 ? 'up' : 'stable'
    };
  };

  const stats = getBMIStats();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiUser} className="text-3xl text-white" />
          </div>
          
          {isEditing ? (
            <div className="space-y-4">
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Full Name"
              />
              <input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Email"
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiSave} className="mr-2" />
                  Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{user.name}</h3>
              <p className="text-gray-600 mb-4">{user.email}</p>
              <button
                onClick={() => setIsEditing(true)}
                className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center mx-auto"
              >
                <SafeIcon icon={FiEdit2} className="mr-2" />
                Edit Profile
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center text-gray-600">
            <SafeIcon icon={FiCalendar} className="mr-3 text-blue-500" />
            <span>Joined {user.joinDate}</span>
          </div>

          {stats && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                <SafeIcon icon={FiTarget} className="mr-2 text-blue-500" />
                BMI Statistics
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">{stats.current}</div>
                  <div className="text-gray-600">Current BMI</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${
                    stats.trend === 'down' ? 'text-green-600' : 
                    stats.trend === 'up' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stats.change > 0 ? '+' : ''}{stats.change}
                  </div>
                  <div className="text-gray-600">Change</div>
                </div>
              </div>
              <div className="mt-3 text-center">
                <div className="text-lg font-semibold text-gray-800">{stats.totalCalculations}</div>
                <div className="text-gray-600 text-sm">Total Calculations</div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UserProfile;